import React, { useEffect, useState } from "react";
import { adminClient } from "../../../lib/admin";
import { toast } from "sonner";
import { CalendarClock, Play, Save, AlertTriangle, CheckCircle2, Plus, X } from "lucide-react";

const Stat = ({ label, value, testid, accent }) => (
  <div className={`border px-3 py-2.5 ${accent ? "border-taven-orange/50 bg-taven-orange/5" : "border-black/10 bg-white"}`} data-testid={testid}>
    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className="text-xl font-black font-display">{value}</p>
  </div>
);

const NumField = ({ label, name, value, step, onChange, hint }) => (
  <label className="block">
    <span className="block text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1">{label}</span>
    <input type="number" step={step || 1} value={value ?? ""} data-testid={`sched-${name}`}
      onChange={(e) => onChange(name, e.target.value === "" ? "" : Number(e.target.value))}
      className="w-full border border-black/15 px-3 py-2 text-sm bg-white outline-none focus:border-black rounded-md" />
    {hint && <span className="block text-[10px] text-muted-foreground mt-1">{hint}</span>}
  </label>
);

const num = (n) => Number(n || 0).toLocaleString();

export default function AdminScheduler() {
  const [cfg, setCfg] = useState(null);
  const [sim, setSim] = useState(null);
  const [live, setLive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ph, setPh] = useState({ handle: "", weekly_target: "" });

  const load = () => adminClient().get("/admin/cj/scheduler").then(({ data }) => {
    setCfg(data.config); setSim(data.simulation); setLive(data.live_calls_enabled);
  }).catch(() => toast.error("Could not load scheduler"));

  useEffect(() => { load(); }, []);

  const set = (name, v) => setCfg((c) => ({ ...c, [name]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await adminClient().put("/admin/cj/scheduler", cfg);
      setCfg(data.config); setSim(data.simulation);
      toast.success("Saved (simulation recalculated)");
    } catch { toast.error("Save failed"); } finally { setBusy(false); }
  };

  const simulate = async () => {
    setBusy(true);
    try {
      const { data } = await adminClient().post("/admin/cj/scheduler/simulate");
      setSim(data.simulation); setCfg(data.config);
      toast.success("Simulation complete — no CJ calls made");
    } catch { toast.error("Simulation failed"); } finally { setBusy(false); }
  };

  const addPriority = () => {
    if (!ph.handle) return;
    set("collection_priorities", [...(cfg.collection_priorities || []), { handle: ph.handle, weekly_target: Number(ph.weekly_target || 0) }]);
    setPh({ handle: "", weekly_target: "" });
  };
  const removePriority = (i) => set("collection_priorities", (cfg.collection_priorities || []).filter((_, x) => x !== i));

  if (!cfg || !sim) return <p className="text-sm text-muted-foreground p-4">Loading scheduler…</p>;

  return (
    <div className="space-y-6" data-testid="admin-scheduler">
      <div className="flex items-start gap-3">
        <CalendarClock className="w-6 h-6 text-taven-orange mt-0.5" />
        <div>
          <h2 className="text-xl font-black font-display">Weekly Catalog Scheduler</h2>
          <p className="text-sm text-muted-foreground">Plan controlled weekly catalog fills. Runs in <strong>simulation mode only</strong> — no CJ calls, no imports, no Shopify writes.</p>
        </div>
      </div>

      {/* Simulation banner */}
      <div className="rounded-xl border border-taven-orange/40 bg-taven-orange/5 p-3 flex items-center gap-2 text-sm" data-testid="sched-mode-banner">
        <AlertTriangle className="w-4 h-4 text-taven-orange" />
        <span><strong>SIMULATION MODE.</strong> Live CJ calls are currently <strong>{live ? "ENABLED" : "DISABLED"}</strong>. Real imports stay off until an admin re-enables CJ live calls.</span>
      </div>

      {/* Results */}
      <div>
        <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Projected weekly capacity</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Stat label="Requested target" value={num(sim.requested_weekly_target)} testid="sim-requested" />
          <Stat label="Realistic capacity" value={num(sim.realistic_weekly_capacity)} testid="sim-realistic" accent />
          <Stat label="Expected accepted" value={num(sim.expected_accepted)} testid="sim-accepted" />
          <Stat label="Rejected / duplicate" value={num(sim.expected_rejected_or_duplicate)} testid="sim-rejected" />
          <Stat label="Est. CJ points needed" value={num(sim.estimated_cj_points_required)} testid="sim-points" />
          <Stat label="Est. API calls" value={num(sim.estimated_api_calls)} testid="sim-calls" />
          <Stat label="Weekly point budget" value={num(sim.weekly_point_budget)} testid="sim-budget" />
          <Stat label="Shortfall vs request" value={num(sim.shortfall_vs_request)} testid="sim-shortfall" />
        </div>
        <div className={`mt-3 rounded-lg p-3 text-sm flex items-center gap-2 ${sim.target_achievable ? "bg-green-50 text-green-800 border border-green-200" : "bg-amber-50 text-amber-900 border border-amber-200"}`} data-testid="sim-verdict">
          {sim.target_achievable ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
          {sim.target_achievable
            ? `The current quota can support the full requested ${num(sim.requested_weekly_target)} products/week.`
            : `The current quota realistically supports about ${num(sim.realistic_weekly_capacity)} approved products/week — ${num(sim.shortfall_vs_request)} short of the ${num(sim.requested_weekly_target)} requested. To hit the full target you would need ~${num(sim.points_to_hit_full_target)} CJ points/week.`}
        </div>
        <p className="text-[10px] text-muted-foreground mt-2">{sim.note}</p>
      </div>

      {/* Config */}
      <div>
        <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Configuration</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <NumField label="Weekly target (max 4,500)" name="weekly_target" value={cfg.weekly_target} onChange={set} hint="Hard-capped at 4,500" />
          <NumField label="Acceptance rate" name="expected_acceptance_rate" value={cfg.expected_acceptance_rate} step={0.01} onChange={set} hint="Fraction surviving dedupe+filters (e.g. 0.05 = 5%)" />
          <NumField label="Points per candidate" name="points_per_candidate" value={cfg.points_per_candidate} onChange={set} hint="Est. CJ points to evaluate one candidate" />
          <NumField label="API calls per candidate" name="calls_per_candidate" value={cfg.calls_per_candidate} onChange={set} hint="list + query + variant/stock" />
          <NumField label="Daily point budget" name="daily_point_budget" value={cfg.daily_point_budget} onChange={set} hint="CJ points available per day" />
          <NumField label="Batch size / run" name="batch_size" value={cfg.batch_size} onChange={set} hint="Products per run when live" />
        </div>

        <label className="flex items-center justify-between gap-3 border border-black/10 bg-white px-3 py-2.5 text-sm mt-3 rounded-md" data-testid="sched-status-toggle">
          <span>Scheduler status <span className="text-muted-foreground">(real runs still require CJ live calls to be enabled)</span></span>
          <select value={cfg.status} onChange={(e) => set("status", e.target.value)} className="border border-black/15 px-3 py-1.5 text-sm rounded-md bg-white">
            <option value="paused">Paused</option>
            <option value="active">Active</option>
          </select>
        </label>
      </div>

      {/* Collection priorities */}
      <div>
        <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Collection priorities</h3>
        <div className="flex flex-wrap gap-2 mb-2">
          <input placeholder="collection handle" value={ph.handle} data-testid="sched-pri-handle"
            onChange={(e) => setPh((p) => ({ ...p, handle: e.target.value }))}
            className="border border-black/15 px-3 py-2 text-sm rounded-md bg-white flex-1 min-w-[160px]" />
          <input type="number" placeholder="weekly target" value={ph.weekly_target} data-testid="sched-pri-target"
            onChange={(e) => setPh((p) => ({ ...p, weekly_target: e.target.value }))}
            className="border border-black/15 px-3 py-2 text-sm rounded-md bg-white w-36" />
          <button onClick={addPriority} data-testid="sched-pri-add" className="px-3 py-2 rounded-md bg-black text-white text-sm font-bold flex items-center gap-1"><Plus className="w-4 h-4" /> Add</button>
        </div>
        <div className="space-y-1">
          {(cfg.collection_priorities || []).map((p, i) => (
            <div key={i} className="flex items-center justify-between border border-black/10 bg-white px-3 py-2 text-sm rounded-md" data-testid={`sched-pri-${i}`}>
              <span><strong>{p.handle}</strong> — {num(p.weekly_target)} / week</span>
              <button onClick={() => removePriority(i)} className="text-muted-foreground hover:text-taven-orange"><X className="w-4 h-4" /></button>
            </div>
          ))}
          {(cfg.collection_priorities || []).length === 0 && <p className="text-xs text-muted-foreground">No collection priorities set.</p>}
        </div>
      </div>

      <div className="flex gap-3">
        <button onClick={save} disabled={busy} data-testid="sched-save-btn" className="px-5 py-2.5 rounded-full bg-black text-white text-sm font-bold flex items-center gap-2 disabled:opacity-50"><Save className="w-4 h-4" /> Save config</button>
        <button onClick={simulate} disabled={busy} data-testid="sched-simulate-btn" className="px-5 py-2.5 rounded-full bg-taven-orange text-white text-sm font-bold flex items-center gap-2 disabled:opacity-50"><Play className="w-4 h-4" /> Run simulation</button>
      </div>
    </div>
  );
}
